import { Injectable } from '@angular/core';
import { DeviceSettingsFeatureToggle } from 'src/app/enums/feature-toggle.enum';
import { DYTCTypes } from 'src/app/enums/intelligent-cooling.enum';
import { LocalStorageKey } from 'src/app/enums/local-storage-key.enum';
import { EMachineType } from 'src/app/enums/machine-type.enum';
import { CommonService } from 'src/app/services/common/common.service';
import { FeatureToggleService } from 'src/app/services/feature-toggle/feature-toggle.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { PowerService } from 'src/app/services/power/power.service';
import { ISectionPresenter } from '../../../models/section-presenter';

@Injectable({
	providedIn: 'root',
})
export class PowerSmartSettingsSection implements ISectionPresenter {
	constructor(
		private commonService: CommonService,
		private featureToggle: FeatureToggleService,
		private powerService: PowerService,
		private logger: LoggerService
	) {}

	public async isVisible(): Promise<boolean> {
		let icAvailable = false;
		try {
			const isFeatureToggleEnabled = this.isFeatureToggleEnabled();

			if (isFeatureToggleEnabled) {
				icAvailable = await this.isIntelligentCoolingVisible();
			}
		} catch (error) {
			this.logger.error('PowerSmartSettingsSection - isVisible - Error', error);
		}

		return icAvailable;
	}

	public isFeatureToggleEnabled() {
		return this.featureToggle.isDeviceSettingsFeatureAvailable(
			DeviceSettingsFeatureToggle.PowerSmartSettings
		);
	}

	public isYoga730(): boolean {
		const cacheMachineFamilyName = this.commonService.getLocalStorageValue(
			LocalStorageKey.MachineFamilyName,
			undefined
		);
		let isYogo730 = false;
		const regExForYoga730 = /YOGA 730/gi;
		if (cacheMachineFamilyName && cacheMachineFamilyName.search(regExForYoga730) !== -1) {
			isYogo730 = true;
		}
		return isYogo730;
	}

	public CheckAvailableDYTC(dytcRevision: number): boolean {
		return (
			dytcRevision === DYTCTypes.DYTC4 ||
			dytcRevision === DYTCTypes.DYTC5 ||
			dytcRevision === DYTCTypes.DYTC6 ||
			dytcRevision === DYTCTypes.DYTC8
		);
	}

	private isIntelligentCoolingVisible(): Promise<boolean> {
		const machineType = this.commonService.getLocalStorageValue(
			LocalStorageKey.MachineType,
			undefined
		);

		switch (machineType) {
			case EMachineType.ThinkPad:
				return this.isIntelligentCoolingVisibleThinkPad();
			case EMachineType.IdeaPad:
				return this.isIntelligentCoolingVisibleIdeaPad();
			default:
				return Promise.resolve(false);
		}
	}

	private async isIntelligentCoolingVisibleThinkPad(): Promise<boolean> {
		const isDriverAvailable = await this.isDriverAvailable();
		if (!isDriverAvailable) {
			return false;
		}

		let icAvailable = await this.isDYTCAvailable();
		if (!icAvailable) {
			icAvailable = await this.isLegacyITSAvailable();
		}

		return icAvailable;
	}

	private async isIntelligentCoolingVisibleIdeaPad(): Promise<boolean> {
		const ITSModeIdeaPad = await this.powerService.getITSModeForICIdeapad();
		return ITSModeIdeaPad?.available;
	}

	private isDriverAvailable(): Promise<boolean> {
		const isYoga730 = this.isYoga730();
		if (isYoga730) {
			return this.powerService.getEMDriverStatus();
		} else {
			return this.powerService.getPMDriverStatus();
		}
	}

	private async isDYTCAvailable(): Promise<boolean> {
		const [itsServiceStatus, its] = await Promise.all([
			this.powerService.getITSServiceStatus(),
			this.powerService.getDYTCRevision(),
		]);

		return itsServiceStatus && this.CheckAvailableDYTC(its);
	}

	private async isLegacyITSAvailable(): Promise<boolean> {
		const [cQLCapability, tIOCapability, legacyManualModeCapability] = await Promise.all([
			this.powerService.getLegacyCQLCapability(),
			this.powerService.getLegacyTIOCapability(),
			this.powerService.getLegacyManualModeCapability(),
		]);
		const hasLegacyITS = cQLCapability || !!tIOCapability || legacyManualModeCapability;
		return hasLegacyITS;
	}
}
